/**
 * Pure view helpers for the Life panel and the fleet card's stage (lane OBS2).
 *
 * The 2026-10-03 live pages said "1 cycle left" for both flies: the projection fed
 * the brain's survival head (≈0.97-0.9996) into a per-cycle death hazard, but BRN
 * trains that head on a 1% drawdown STATE, not on death. The publisher's model v2
 * no longer does that; these helpers render what it publishes instead — the real
 * distance to economic death, the head as a labelled estimate with its measured
 * calibration, and why a life is still "unbound".
 */

import type {
  LifeBinding,
  LifeCapitalAlert,
  LifeDocument,
  LifeEconomicDeath,
  LifeProjection,
  LifeSurvivalHead,
} from "../types/incremental";
import { formatCount, formatDecimal, formatMoney, formatPercent } from "../format/number";

export interface LifeRow {
  readonly plainLabel: string;
  readonly technicalLabel?: string;
  readonly tooltip: string;
  readonly value: string;
}

/** Content key for the poller: the life object is small and content-addressed. */
export function lifeFingerprint(doc: LifeDocument | null): string {
  return doc ? JSON.stringify(doc) : "none";
}

/** The projection never resolved within its horizon for most draws. */
export function isCensored(p: LifeProjection): boolean {
  return p.censored_share !== null && p.censored_share >= 0.5;
}

function horizonOf(p: LifeProjection): number | null {
  const raw = p.inputs["horizon_cycles"];
  return typeof raw === "number" ? raw : null;
}

/** "Typical time left": "> 2,000 cycles" when the median is the horizon itself
 * (no modelled death inside it), never a bare horizon that reads like a forecast. */
/** A P10/P90 cycle count; "> N" when it is the horizon itself (a censored draw). */
export function cyclesText(value: number | null, p: LifeProjection): string {
  if (value === null) return "–";
  const horizon = horizonOf(p);
  if (horizon !== null && value >= horizon) return `> ${formatCount(horizon)}`;
  return formatDecimal(value);
}

export function timeLeftText(p: LifeProjection): string {
  if (p.median_cycles === null) return "not modeled";
  const horizon = horizonOf(p);
  if (isCensored(p) && horizon !== null && p.median_cycles >= horizon) {
    return `> ${formatCount(horizon)} cycles (no modeled death within the horizon)`;
  }
  return `${formatDecimal(p.median_cycles)} cycles`;
}

const CAPITAL_ALERT_TOOLTIP =
  "D6 interim alarm (until the A1 governor fix): the runner writes one durable risk.capital_alert " +
  "event and an ERROR log line when W(t) falls below the alert line (a fraction of the episode's " +
  "opening trading capital, 93% by default), and one more when it climbs back above the recovery " +
  "line. It changes nothing about trading; the equities governor's distress clamp engages at 90%.";

function ratioPercent(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return "–";
  const value = Number(raw);
  return Number.isFinite(value) ? formatPercent(value, 2) : "–";
}

/** D6: the capital-alert rows (empty when no crossing was ever recorded). */
export function capitalAlertRows(alert: LifeCapitalAlert | null | undefined): LifeRow[] {
  if (!alert) return [];
  const status = alert.active
    ? `BELOW the ${ratioPercent(alert.alert_fraction)} line` +
      (alert.since ? ` since ${alert.since}` : "")
    : `recovered (back above ${ratioPercent(alert.recovery_fraction)})` +
      (alert.event_time ? ` at ${alert.event_time}` : "");
  return [
    {
      plainLabel: "Capital alert",
      technicalLabel: "risk.capital_alert",
      tooltip: CAPITAL_ALERT_TOOLTIP,
      value: status,
    },
    {
      plainLabel: "Money vs opening",
      technicalLabel: "W / opening capital",
      tooltip:
        `W(t) at the last crossing as a share of the opening trading capital ` +
        `${formatMoney(alert.opening_trading_capital)}.`,
      value: `${ratioPercent(alert.capital_ratio)} (${formatMoney(alert.resources)})`,
    },
    {
      plainLabel: "Alert line",
      technicalLabel: "fraction × opening",
      tooltip: CAPITAL_ALERT_TOOLTIP,
      value: formatMoney(alert.alert_line),
    },
  ];
}

/** D6: the fleet card's alert pill text, or `null` when no alert is active. */
export function fleetCapitalAlertText(flag: boolean | null | undefined): string | null {
  return flag === true ? "Capital alert" : null;
}

export const FLEET_CAPITAL_ALERT_TOOLTIP = CAPITAL_ALERT_TOOLTIP;

export function economicDeathRows(e: LifeEconomicDeath | undefined): LifeRow[] {
  if (!e) return [];
  const source =
    e.floor_source === "life.assessment.solvency"
      ? "the economic-death gate's own last assessment"
      : e.floor_source === "unknown_opening_capital"
        ? "unknown — the episode's opening capital is not published, so no floor is shown"
        : e.floor_source === "derived_tariff_only"
          ? "the tariff floor alone (this risk policy has no capital floor)"
          : "derived from the gate's rule (the gate has not reported one this episode)";
  const enforcement = e.enforced
    ? "The gate runs from the episode's first wake, bound or not; death commits once a breach is confirmed (3 consecutive breaching cycles for a risk-governed parent)."
    : "The gate is not running: this life is past its death (settling, dead or archived) or its stage is unknown.";
  return [
    {
      plainLabel: "Money left",
      technicalLabel: "W(t)",
      tooltip:
        "Economic resources W(t): broker equity minus accrued liabilities minus the liquidation estimate.",
      value: formatMoney(e.resources),
    },
    {
      plainLabel: "Death floor",
      technicalLabel: "solvency floor",
      tooltip:
        `${e.floor_rule}. Death fraction ${e.death_fraction ?? "–"} × opening trading capital ` +
        `${formatMoney(e.opening_trading_capital)} = ${formatMoney(e.capital_floor)}; 24h ` +
        `burn ${formatMoney(e.tariff_floor)}. Source: ${source}. ${enforcement}`,
      value: formatMoney(e.floor),
    },
    {
      plainLabel: "Room above the floor",
      technicalLabel: "W − floor",
      tooltip:
        "How far W(t) can fall before economic death: dollars, and the fraction of W(t). " +
        enforcement,
      value:
        e.distance === null
          ? "–"
          : `${formatMoney(e.distance)} (W can fall ${formatPercent(e.fall_to_floor, 1)})` +
            (e.enforced ? "" : " · gate not running"),
    },
  ];
}

const CALIBRATION_WORDS: Readonly<Record<string, string>> = {
  insufficient_data: "not yet calibrated",
  calibrated: "calibrated",
  miscalibrated: "miscalibrated",
};

export function survivalHeadRows(h: LifeSurvivalHead | undefined): LifeRow[] {
  if (!h) return [];
  const c = h.calibration;
  const state = CALIBRATION_WORDS[c.state] ?? c.state;
  const evidence =
    c.samples > 0
      ? `predicted ${formatPercent(c.mean_predicted, 1)} vs realized ${formatPercent(c.realized_rate, 1)} over ${formatCount(c.samples)} wakes`
      : "no scored wakes yet";
  return [
    {
      plainLabel: "Brain's drawdown estimate",
      technicalLabel: "survival head",
      tooltip:
        `An estimate, not a death clock: the survival head is trained on ${h.target}. ` +
        `Its live output is scored against that label at the next wake; ` +
        `${c.state === "insufficient_data" ? `a verdict needs ${formatCount(c.min_samples)} scored wakes` : `|predicted − realized| ≤ ${formatPercent(c.tolerance, 0)} reads calibrated`}. ` +
        "Not used in the life-expectancy projection.",
      value: h.hazard === null ? "–" : `${formatPercent(h.hazard, 1)} (estimate)`,
    },
    {
      plainLabel: "Estimate calibration",
      technicalLabel: "vs realized label",
      tooltip:
        "The head's mean prediction against how often the label actually happened this episode " +
        `(W more than ${formatPercent(h.drawdown_threshold, 0)} below its running peak at the next wake)` +
        (c.brier === null ? "." : `; Brier score ${formatDecimal(c.brier, 3)}.`),
      value: `${state} · ${evidence}`,
    },
  ];
}

/** "unbound · awaiting regime confirmation (1/3 days)" — the stage with its reason. */
export function stageText(
  stage: string | null | undefined,
  note: string | null | undefined,
): string {
  if (!stage) return "–";
  return note ? `${stage} · ${note}` : stage;
}

/** The fleet card's narrow cell: "unbound · 1/3 days" (the full note goes in the
 * tooltip). */
export function compactStageText(
  stage: string | null | undefined,
  note: string | null | undefined,
): string {
  if (!stage) return "–";
  const progress = note ? /\((\d+\/\d+ days)\)/.exec(note)?.[1] : undefined;
  return progress ? `${stage} · ${progress}` : stage;
}

export function bindingNote(binding: LifeBinding | undefined): string | null {
  if (!binding || binding.status !== "awaiting_regime_confirmation") return null;
  if (binding.startup_streak !== null && binding.entry_confirmations !== null) {
    return `awaiting regime confirmation (${binding.startup_streak}/${binding.entry_confirmations} days)`;
  }
  return "awaiting regime confirmation";
}

/** The stage tooltip, with the contract's entry rule from the published binding when
 * present (review: never a hard-coded threshold that could drift from the contract). */
export function stageTooltip(b: LifeBinding | undefined): string {
  const probability = b?.entry_probability;
  const confirmations = b?.entry_confirmations;
  if (typeof probability !== "number" || typeof confirmations !== "number") return STAGE_TOOLTIP;
  return (
    "Lifecycle stage. A fresh life starts UNBOUND and binds (→ active) once the daily regime " +
    `detector confirms a regime: its dominant state at posterior ≥ ${probability} on ` +
    `${confirmations} consecutive valid daily frames. Trading runs either way; natural and ` +
    "economic death are judged only for a bound life."
  );
}

export const STAGE_TOOLTIP =
  "Lifecycle stage. A fresh life starts UNBOUND and binds (→ active) once the daily regime " +
  "detector confirms a regime: its dominant state at posterior ≥ 0.8 on 3 consecutive valid " +
  "daily frames (the contract's lifecycle.natural_death entry rule). Trading runs either way; natural and economic death are judged only for a " +
  "bound life.";

export function bindingRows(
  stage: string | null | undefined,
  b: LifeBinding | undefined,
): LifeRow[] {
  const rows: LifeRow[] = [
    {
      plainLabel: "Stage",
      technicalLabel: "lifecycle",
      tooltip: stageTooltip(b),
      value: stageText(stage, bindingNote(b)),
    },
  ];
  if (b && b.status !== "bound" && b.status !== "ended" && b.dominant_state !== null) {
    rows.push({
      plainLabel: "Regime detector",
      technicalLabel: "dominant state",
      tooltip:
        `${b.rule}. Read from the runner's persisted detector snapshot` +
        (b.as_of ? ` (as of ${b.as_of.slice(0, 16).replace("T", " ")}Z).` : "."),
      value: `${b.dominant_state} at ${formatPercent(b.dominant_posterior, 1)}`,
    });
  }
  return rows;
}
